import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign, Users, Building2, Briefcase, TrendingUp, CreditCard, Plus,
  Activity, AlertTriangle, CheckCircle, Clock, ArrowUpRight, ArrowDownRight,
  Globe, Shield, Eye, BarChart3, PieChart as PieChartIcon,
  Target, ArrowRightLeft, ClipboardCheck, Droplets, Zap
} from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import AmazonQChat from '../chat/AmazonQChat';
import { db } from '../../services/DatabaseService';
import { useAuth } from '../../context/AuthContext';
import { PILLARS } from '../../config/pillars';

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, trend, subValue }) => {
  return (
    <div className="bg-white rounded-none border border-zinc-200 p-5 hover:border-[#4D4D4D] transition-all">
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 border border-zinc-300 bg-zinc-50 rounded-none flex items-center justify-center">
          <Icon className="w-4 h-4 text-black" />
        </div>
        {trend !== undefined && (
          <div className="flex items-center gap-1 font-mono text-xs px-2 py-0.5 border border-zinc-300 bg-zinc-50 text-black rounded-none">
            {trend >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="font-sans text-2xl font-bold text-black">{value}</p>
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 mt-1">{label}</p>
        {subValue && <p className="font-mono text-[10px] text-zinc-500 mt-1">{subValue}</p>}
      </div>
    </div>
  );
};

// Badge Component
const Badge = ({ children, variant = 'default' }) => {
  return (
    <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider rounded-none border border-zinc-300 bg-zinc-50 text-black font-medium">
      {children}
    </span>
  );
};

// Tenant Card Component
const TenantCard = ({ tenant, type }) => {
  const isBank = type === 'bank';
  
  return (
    <div className="bg-white rounded-none border border-zinc-200 p-5 hover:border-[#4D4D4D] transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border border-zinc-300 bg-zinc-50 rounded-none flex items-center justify-center">
            {isBank ? <Building2 className="w-4 h-4 text-black" /> : <Briefcase className="w-4 h-4 text-black" />}
          </div>
          <div>
            <h4 className="font-sans font-bold text-sm text-black">{tenant.name}</h4>
            <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">{tenant.country} • {tenant.email}</p>
          </div>
        </div>
        <Badge variant={tenant.status === 'active' ? 'success' : 'warning'}>
          {tenant.status}
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-zinc-100 font-mono text-xs">
        {isBank ? (
          <>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400">Active Loans</p>
              <p className="font-bold text-black">-</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400">NPL Rate</p>
              <p className="font-bold text-black">-</p>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400">AUM</p>
              <p className="font-bold text-black">${((tenant.aum || 0) / 1000000).toFixed(0)}M</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400">Investments</p>
              <p className="font-bold text-black">-</p>
            </div>
          </>
        )}
      </div>
      
      <div className="flex gap-2 mt-4">
        <button className="flex-1 py-2 px-3 bg-transparent border border-zinc-300 text-black font-mono text-xs uppercase tracking-wider hover:border-[#4D4D4D] transition-colors rounded-none">
          Details
        </button>
        <button className="flex-1 py-2 px-3 bg-[#4D4D4D] border border-[#4D4D4D] text-white font-mono text-xs uppercase tracking-wider hover:bg-black transition-colors rounded-none">
          Manage
        </button>
      </div>
    </div>
  );
};

const ForwardsflowAdminDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadData = async () => {
      try {
        const dashboardData = await db.getDashboardData(user?.role || 'forwardsflow_admin', user?.tenantId || 'default');
        setData(dashboardData);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] font-sans">
        <div className="w-8 h-8 border-2 border-[#4D4D4D] border-t-transparent rounded-full animate-spin"></div>
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mt-4">Loading system telemetry...</p>
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const banks = data?.banks || [];
  const investors = data?.investors || [];

  const stats = [
    { icon: DollarSign, label: 'Total Capital Deployed', value: `$${((metrics.totalCapitalDeployed || 0) / 1000000).toFixed(1)}M`, trend: metrics.monthlyGrowth },
    { icon: Building2, label: 'Partner Banks', value: banks.length.toString(), subValue: `${banks.filter(b => b.status === 'active').length} active` },
    { icon: Briefcase, label: 'Impact Investors', value: investors.length.toString(), subValue: `${investors.filter(i => i.status === 'active').length} active` },
    { icon: CreditCard, label: 'Active Mobile Loans', value: (metrics.totalActiveLoans || 0).toLocaleString(), trend: 12.1 },
    { icon: TrendingUp, label: 'Platform Yield', value: `${metrics.avgPlatformYield || 0}%` },
    { icon: DollarSign, label: 'Monthly Revenue', value: `$${((metrics.monthlyRevenue || 0) / 1000).toFixed(0)}K`, trend: metrics.monthlyGrowth },
  ];

  return (
    <div className="space-y-8 font-sans text-black antialiased">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-200 pb-6">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-1">Super Admin Console</span>
          <h1 className="font-sans text-3xl font-bold text-black">Platform Dashboard</h1>
          <p className="font-sans text-xs text-zinc-600 mt-1">Forwardsflow System Administration & Multi-Tenant Control</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2.5 border border-zinc-300 rounded-none hover:border-[#4D4D4D] font-mono text-xs uppercase tracking-[0.15em] text-black transition-all flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5 text-black" />
            Reports
          </button>
          <button className="px-5 py-2.5 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all flex items-center gap-2">
            <Plus className="w-3.5 h-3.5 text-white" />
            Add Tenant
          </button>
        </div>
      </div>

      {/* Platform Notice */}
      <div className="bg-zinc-50 border border-zinc-300 rounded-none p-4 flex items-center gap-4">
        <div className="w-9 h-9 border border-zinc-300 bg-white rounded-none flex items-center justify-center flex-shrink-0">
          <Shield className="w-4 h-4 text-black" />
        </div>
        <div className="flex-1">
          <p className="font-sans text-sm font-bold text-black">Platform Administration Mode</p>
          <p className="font-sans text-xs text-zinc-600">Managing multi-tenant organizations. Individual tenant employee data is restricted under strict privacy compliance policies.</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {stats.map((stat, idx) => <StatCard key={idx} {...stat} />)}
      </div>

      {/* Pillar Health Strip */}
      <div className="bg-white rounded-none border border-zinc-200 p-6">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-4">Pillar Health</span>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {PILLARS.map(pillar => {
            const ICON_MAP = { Target, ArrowRightLeft, ClipboardCheck, Shield, Droplets, Zap, Briefcase };
            const Icon = ICON_MAP[pillar.icon] || Activity;
            // Demo metric values — keyed by adminMetric
            const DEMO_METRICS = {
              markets_live: { value: '5', status: 'healthy' },
              open_fx_rfqs: { value: '8', status: 'healthy' },
              kyc_backlog: { value: '3', status: 'warning' },
              open_hedges_notional: { value: '$18.5M', status: 'healthy' },
              maturing_30d: { value: '12', status: 'warning' },
              settlements_today: { value: '4', status: 'healthy' },
              aum: { value: '$42.5M', status: 'healthy' },
            };
            const metric = DEMO_METRICS[pillar.adminMetric] || { value: '—', status: 'idle' };
            const statusColor = metric.status === 'healthy' ? 'bg-emerald-500' : metric.status === 'warning' ? 'bg-amber-500' : 'bg-zinc-300';
            return (
              <div key={pillar.key} className="border border-zinc-200 rounded-none p-3 hover:border-[#4D4D4D] transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-3.5 h-3.5 text-zinc-500" />
                  <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-zinc-400 truncate">{pillar.short}</span>
                  <span className={`w-2 h-2 rounded-full ml-auto flex-shrink-0 ${statusColor}`} />
                </div>
                <p className="font-mono text-lg font-bold text-black">{metric.value}</p>
                <p className="font-mono text-[9px] text-zinc-400 mt-0.5">{pillar.adminMetric.replace(/_/g, ' ')}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-none border border-zinc-200 p-6 hover:border-[#4D4D4D] transition-all">
          <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-1">Volume Growth</span>
          <h3 className="font-sans text-lg font-bold text-black mb-4">Capital Deployed (6 Months)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={metrics.capitalByMonth || []}>
              <defs>
                <linearGradient id="colorCapital" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4D4D4D" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#4D4D4D" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 11, fontFamily: 'monospace' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 11, fontFamily: 'monospace' }} tickFormatter={(v) => `$${(v / 1000000).toFixed(0)}M`} />
              <Tooltip formatter={(v) => [`$${(v / 1000000).toFixed(2)}M`, 'Capital']} />
              <Area type="monotone" dataKey="capital" stroke="#4D4D4D" strokeWidth={2} fill="url(#colorCapital)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-none border border-zinc-200 p-6 hover:border-[#4D4D4D] transition-all">
          <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-1">Fee Income</span>
          <h3 className="font-sans text-lg font-bold text-black mb-4">Monthly Revenue Stream</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={metrics.revenueByMonth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 11, fontFamily: 'monospace' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#71717a', fontSize: 11, fontFamily: 'monospace' }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
              <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Revenue']} />
              <Bar dataKey="revenue" fill="#4D4D4D" radius={[0, 0, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-none border border-zinc-200">
        <div className="flex border-b border-zinc-200 bg-zinc-50 overflow-x-auto">
          {['overview', 'banks', 'investors', 'compliance'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors rounded-none border-r border-zinc-200 ${
                activeTab === tab
                  ? 'bg-white text-black font-bold border-b-2 border-b-[#4D4D4D]'
                  : 'text-zinc-600 hover:text-black hover:bg-zinc-100'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'banks' && ` (${banks.length})`}
              {tab === 'investors' && ` (${investors.length})`}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Partner Banks */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-sans font-bold text-base text-black">Partner Banks</h4>
                  <Link to="/admin/banks" className="font-mono text-xs uppercase tracking-wider text-black underline font-bold">View all</Link>
                </div>
                <div className="space-y-3">
                  {banks.slice(0, 3).map(bank => (
                    <TenantCard key={bank.id} tenant={bank} type="bank" />
                  ))}
                </div>
              </div>

              {/* Impact Investors */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-sans font-bold text-base text-black">Impact Investors</h4>
                  <Link to="/admin/investors" className="font-mono text-xs uppercase tracking-wider text-black underline font-bold">View all</Link>
                </div>
                <div className="space-y-3">
                  {investors.slice(0, 3).map(investor => (
                    <TenantCard key={investor.id} tenant={investor} type="investor" />
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'banks' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {banks.map(bank => (
                <TenantCard key={bank.id} tenant={bank} type="bank" />
              ))}
            </div>
          )}

          {activeTab === 'investors' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {investors.map(investor => (
                <TenantCard key={investor.id} tenant={investor} type="investor" />
              ))}
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-zinc-50 border border-zinc-300 rounded-none p-4">
                  <div className="flex items-center gap-2 text-zinc-600 mb-2 font-mono text-xs uppercase tracking-wider">
                    <CheckCircle className="w-4 h-4 text-black" />
                    <span>Compliant Banks</span>
                  </div>
                  <p className="font-sans text-2xl font-bold text-black">{banks.filter(b => b.status === 'active').length}</p>
                </div>
                <div className="bg-zinc-50 border border-zinc-300 rounded-none p-4">
                  <div className="flex items-center gap-2 text-zinc-600 mb-2 font-mono text-xs uppercase tracking-wider">
                    <AlertTriangle className="w-4 h-4 text-black" />
                    <span>AML Alerts</span>
                  </div>
                  <p className="font-sans text-2xl font-bold text-black">5</p>
                </div>
                <div className="bg-zinc-50 border border-zinc-300 rounded-none p-4">
                  <div className="flex items-center gap-2 text-zinc-600 mb-2 font-mono text-xs uppercase tracking-wider">
                    <Clock className="w-4 h-4 text-black" />
                    <span>Pending KYC</span>
                  </div>
                  <p className="font-sans text-2xl font-bold text-black">2</p>
                </div>
                <div className="bg-zinc-50 border border-zinc-300 rounded-none p-4">
                  <div className="flex items-center gap-2 text-zinc-600 mb-2 font-mono text-xs uppercase tracking-wider">
                    <Activity className="w-4 h-4 text-black" />
                    <span>Transactions Today</span>
                  </div>
                  <p className="font-sans text-2xl font-bold text-black">147</p>
                </div>
              </div>

              <div>
                <h4 className="font-sans font-bold text-base text-black mb-4">Recent Compliance Activity Log</h4>
                <div className="space-y-2">
                  {[
                    { action: 'KYC Verified', entity: 'Impact Capital Partners', time: '2 hours ago' },
                    { action: 'AML Alert Triggered', entity: 'Transaction TXN-2024-00123', time: '4 hours ago' },
                    { action: 'License Renewed', entity: 'Equity Africa Bank', time: '1 day ago' },
                    { action: 'Document Expired', entity: 'Shell Foundation', time: '2 days ago' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-zinc-50 border border-zinc-200 rounded-none">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-[#4D4D4D] rounded-none" />
                        <div>
                          <p className="font-sans text-xs font-bold text-black">{item.action}</p>
                          <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">{item.entity}</p>
                        </div>
                      </div>
                      <span className="font-mono text-[10px] text-zinc-400">{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fee Waterfall Summary */}
      <div className="bg-white rounded-none border border-zinc-200 p-6 hover:border-[#4D4D4D] transition-all">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-1">Economics</span>
        <h3 className="font-sans text-lg font-bold text-black mb-4">Fee Waterfall Matrix (per $1M investment)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 font-mono text-xs">
          {[
            { label: 'Foreign Exchange Commission', value: '$10,000', pct: '1%' },
            { label: 'Forwards Brokerage', value: '$10,000', pct: '1%' },
            { label: 'Deposit Fee', value: '$10,000', pct: '1%' },
            { label: 'Settlement Fee', value: '$10,000', pct: '1%' },
            { label: 'Forwards Premium', value: '$200,000', pct: '20%' },
            { label: 'SWIFT Fee', value: '$25', pct: 'flat' },
            { label: 'Account Origination', value: '$1,175', pct: '$25/acct' },
            { label: 'Total FF Revenue', value: '$241,200', pct: '24.1%', highlight: true },
          ].map((item, i) => (
            <div key={i} className={`rounded-none p-3 text-center border ${item.highlight ? 'bg-[#4D4D4D] text-white border-[#4D4D4D]' : 'bg-zinc-50 border-zinc-200 text-black'}`}>
              <p className={`text-[10px] uppercase tracking-wider mb-1 ${item.highlight ? 'text-zinc-300' : 'text-zinc-500'}`}>{item.label}</p>
              <p className="font-bold text-sm">{item.value}</p>
              <p className={`text-[10px] ${item.highlight ? 'text-zinc-300' : 'text-zinc-400'}`}>{item.pct}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Platform P&L Summary */}
      <div className="bg-[#4D4D4D] text-white border border-[#4D4D4D] p-8 rounded-none">
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 block mb-2">Financial Auditing</span>
        <h3 className="font-sans text-2xl font-bold text-white mb-6">Platform P&L Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 font-mono">
          <div className="border-l-2 border-white/40 pl-4">
            <p className="text-xs uppercase tracking-wider text-zinc-300">Gross Revenue</p>
            <p className="text-3xl font-bold text-white mt-1">${((metrics.monthlyRevenue || 0) * 6 / 1000).toFixed(0)}K</p>
            <p className="text-[10px] text-zinc-300 mt-1">+{metrics.monthlyGrowth || 0}% MoM</p>
          </div>
          <div className="border-l-2 border-white/40 pl-4">
            <p className="text-xs uppercase tracking-wider text-zinc-300">Origination Fees</p>
            <p className="text-3xl font-bold text-white mt-1">$31.2K</p>
            <p className="text-[10px] text-zinc-300 mt-1">1,247 accounts YTD</p>
          </div>
          <div className="border-l-2 border-white/40 pl-4">
            <p className="text-xs uppercase tracking-wider text-zinc-300">Operating Costs</p>
            <p className="text-3xl font-bold text-white mt-1">$245K</p>
            <p className="text-[10px] text-zinc-300 mt-1">Infrastructure + Team</p>
          </div>
          <div className="border-l-2 border-white/40 pl-4">
            <p className="text-xs uppercase tracking-wider text-zinc-300">Net Profit</p>
            <p className="text-3xl font-bold text-white mt-1">$536K</p>
            <p className="text-[10px] text-zinc-300 mt-1">68% margin</p>
          </div>
          <div className="border-l-2 border-white/40 pl-4">
            <p className="text-xs uppercase tracking-wider text-zinc-300">LTV:CAC Ratio</p>
            <p className="text-3xl font-bold text-white mt-1">8.2x</p>
            <p className="text-[10px] text-zinc-300 mt-1">Target: 5x</p>
          </div>
        </div>
      </div>

      {/* Amazon Q AI Assistant */}
      <AmazonQChat userRole="platform_admin" tenantId="default" />
    </div>
  );
};

export default ForwardsflowAdminDashboard;
